<template>
  <!-- The resume as a sheet of A4. It is drawn at its real size (794 x 1123 px at 96 dpi) and
       scaled down to fit, so line breaks here are the ones the printed page gets. -->
  <div ref="frame" class="w-full">
    <div class="mx-auto" :style="{ width: `${PAGE_WIDTH * scale}px`, height: `${Math.max(height, PAGE_HEIGHT) * scale}px` }">
      <article ref="sheet" class="resume-sheet bg-white text-gray-900 shadow-lg ring-1 ring-black/5 origin-top-left" :style="sheetStyle" :data-template="template">
        <ResumeSheetBody :content="content" :template="template" :picture-url="pictureUrl" />
      </article>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import ResumeSheetBody from './ResumeSheetBody.vue'
import { PAGE_HEIGHT, PAGE_WIDTH, horizontalPaddingOf, paddingOf, sheetBackground, typography } from './resumeLayout.js'

const props = defineProps({
  content: { type: Object, required: true },
  template: { type: String, default: 'modern' },
  pictureUrl: { type: String, default: null },
  maxScale: { type: Number, default: 1 }, // 1 never draws the sheet bigger than a printed page; the full screen view allows more
})
const emit = defineEmits(['pages'])

const frame = ref(null)
const sheet = ref(null)
const scale = ref(1)
const height = ref(PAGE_HEIGHT)

const sheetStyle = computed(() => ({
  ...typography(props.content.settings),
  width: `${PAGE_WIDTH}px`,
  minHeight: `${PAGE_HEIGHT}px`,
  padding: `${paddingOf(props.content.settings)}px ${horizontalPaddingOf(props.template, props.content.settings)}px`,
  ...sheetBackground(props.template, props.content.settings),
  transform: `scale(${scale.value})`,
}))

// Fit the sheet to the panel, and report how many printed pages it comes to.
function measure() {
  if (frame.value) scale.value = Math.min(props.maxScale, frame.value.clientWidth / PAGE_WIDTH) || 1
  if (sheet.value) height.value = sheet.value.offsetHeight
  emit('pages', Math.max(1, Math.ceil((height.value - 4) / PAGE_HEIGHT)))
}

let observer = null
onMounted(() => {
  measure()
  if (typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(measure)
  observer.observe(frame.value)
  observer.observe(sheet.value)
})
onBeforeUnmount(() => observer?.disconnect())
watch(() => props.content, () => nextTick(measure), { deep: true })
</script>
